import React, { useEffect, useState } from "react";
import { useNode } from "@craftjs/core";
import { deviceBreakpoints } from "../../App";
import "../../styles/landing.css";

// Define allowed flex direction types
type FlexDirectionType = "row" | "column" | "row-reverse" | "column-reverse";

interface ResponsiveContainerProps {
  children: React.ReactNode;
  className?: string;
  mobileStyles?: React.CSSProperties & {
    flexDirection?: FlexDirectionType;
  };
  tabletStyles?: React.CSSProperties & {
    flexDirection?: FlexDirectionType;
  };
  laptopStyles?: React.CSSProperties & {
    flexDirection?: FlexDirectionType;
  };
  desktopStyles?: React.CSSProperties & {
    flexDirection?: FlexDirectionType;
  };
  padding?: string;
  margin?: string;
  background?: string;
  borderRadius?: number;
  boxShadow?: string;
  border?: string;
  gap?: string;
}

export const ResponsiveContainer = ({
  children,
  className,
  mobileStyles = {},
  tabletStyles = {},
  laptopStyles = {},
  desktopStyles = {},
  padding = "0",
  margin = "0",
  background,
  borderRadius,
  boxShadow,
  border,
  gap,
}: ResponsiveContainerProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const [currentStyles, setCurrentStyles] = useState<React.CSSProperties>({});
  const [screenSize, setScreenSize] = useState<string>("desktop");

  // Function to determine current screen size and set appropriate styles
  const handleWindowResize = () => {
    const width = window.innerWidth;

    if (width <= 425) {
      setScreenSize("mobile");
      setCurrentStyles(mobileStyles);
    } else if (width <= 768) {
      setScreenSize("tablet");
      setCurrentStyles(tabletStyles);
    } else if (width <= 1024) {
      setScreenSize("laptop");
      setCurrentStyles(laptopStyles);
    } else {
      setScreenSize("desktop");
      setCurrentStyles(desktopStyles);
    }
  };

  // Set initial styles and add resize listener
  useEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [mobileStyles, tabletStyles, laptopStyles, desktopStyles]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Connect the ref for drag and drop
  React.useEffect(() => {
    if (containerRef.current) {
      connect(drag(containerRef.current));
    }
  }, [connect, drag]);

  return (
    <div
      ref={containerRef}
      className={`responsive-container ${className || ""} ${
        selected ? "selected-component" : ""
      }`}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding,
        margin,
        background,
        borderRadius:
          borderRadius !== undefined ? `${borderRadius}px` : undefined,
        boxShadow,
        border,
        gap,
        position: "relative",
        outline: selected ? "2px solid #0f3460" : "none",
        ...currentStyles,
      }}
    >
      {selected && (
        <div
          className="component-selected-indicator"
          style={{
            position: "absolute",
            top: "-16px",
            left: "0",
            background: "#0f3460",
            color: "white",
            fontSize: "10px",
            padding: "2px 4px",
            borderRadius: "3px 3px 0 0",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          Responsive Container ({screenSize})
        </div>
      )}
      {children}
    </div>
  );
};

export const ResponsiveContainerSettings = () => {
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props || {},
  }));

  return (
    <div className="settings-container">
      <h4>Responsive Container Settings</h4>

      <div className="form-group">
        <label>Padding</label>
        <input
          type="text"
          className="form-control"
          value={props.padding || "0"}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) =>
                (props.padding = e.target.value)
            )
          }
          placeholder="e.g. 10px"
        />
      </div>

      <div className="form-group">
        <label>Margin</label>
        <input
          type="text"
          className="form-control"
          value={props.margin || "0"}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) =>
                (props.margin = e.target.value)
            )
          }
          placeholder="e.g. 10px"
        />
      </div>

      <div className="form-group">
        <label>Background Color</label>
        <input
          type="text"
          className="form-control"
          value={props.background || ""}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) =>
                (props.background = e.target.value)
            )
          }
          placeholder="e.g. #ffffff"
        />
      </div>

      <div className="form-group">
        <label>Border Radius</label>
        <input
          type="number"
          className="form-control"
          value={props.borderRadius || 0}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) =>
                (props.borderRadius = e.target.valueAsNumber)
            )
          }
          placeholder="e.g. 5"
        />
      </div>

      <div className="form-group">
        <label>Box Shadow</label>
        <input
          type="text"
          className="form-control"
          value={props.boxShadow || ""}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) =>
                (props.boxShadow = e.target.value)
            )
          }
          placeholder="e.g. 0px 4px 8px rgba(0, 0, 0, 0.1)"
        />
      </div>

      <div className="form-group">
        <label>Border</label>
        <input
          type="text"
          className="form-control"
          value={props.border || ""}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) =>
                (props.border = e.target.value)
            )
          }
          placeholder="e.g. 1px solid #000000"
        />
      </div>

      <div className="form-group">
        <label>Gap</label>
        <input
          type="text"
          className="form-control"
          value={props.gap || ""}
          onChange={(e) =>
            setProp(
              (props: ResponsiveContainerProps) => (props.gap = e.target.value)
            )
          }
          placeholder="e.g. 10px"
        />
      </div>

      <h5>Mobile Styles (≤ 425px)</h5>
      <div className="form-group">
        <label>Flex Direction</label>
        <select
          className="form-control"
          value={props.mobileStyles?.flexDirection || "column"}
          onChange={(e) =>
            setProp((props: ResponsiveContainerProps) => {
              props.mobileStyles = {
                ...props.mobileStyles,
                flexDirection: e.target.value as FlexDirectionType,
              };
            })
          }
        >
          <option value="column">Column</option>
          <option value="row">Row</option>
        </select>
      </div>

      <h5>Tablet Styles (426px - 768px)</h5>
      <div className="form-group">
        <label>Flex Direction</label>
        <select
          className="form-control"
          value={props.tabletStyles?.flexDirection || "column"}
          onChange={(e) =>
            setProp((props: ResponsiveContainerProps) => {
              props.tabletStyles = {
                ...props.tabletStyles,
                flexDirection: e.target.value as FlexDirectionType,
              };
            })
          }
        >
          <option value="column">Column</option>
          <option value="row">Row</option>
        </select>
      </div>

      <h5>Laptop Styles (769px - 1024px)</h5>
      <div className="form-group">
        <label>Flex Direction</label>
        <select
          className="form-control"
          value={props.laptopStyles?.flexDirection || "row"}
          onChange={(e) =>
            setProp((props: ResponsiveContainerProps) => {
              props.laptopStyles = {
                ...props.laptopStyles,
                flexDirection: e.target.value as FlexDirectionType,
              };
            })
          }
        >
          <option value="column">Column</option>
          <option value="row">Row</option>
        </select>
      </div>

      <h5>Desktop Styles (&gt; 1024px)</h5>
      <div className="form-group">
        <label>Flex Direction</label>
        <select
          className="form-control"
          value={props.desktopStyles?.flexDirection || "row"}
          onChange={(e) =>
            setProp((props: ResponsiveContainerProps) => {
              props.desktopStyles = {
                ...props.desktopStyles,
                flexDirection: e.target.value as FlexDirectionType,
              };
            })
          }
        >
          <option value="column">Column</option>
          <option value="row">Row</option>
        </select>
      </div>
    </div>
  );
};

ResponsiveContainer.craft = {
  displayName: "Responsive Container",
  props: {
    padding: "0",
    margin: "0",
    mobileStyles: {
      flexDirection: "column" as FlexDirectionType,
      alignItems: "center",
    },
    tabletStyles: {
      flexDirection: "column" as FlexDirectionType,
      alignItems: "center",
    },
    laptopStyles: {
      flexDirection: "row" as FlexDirectionType,
      alignItems: "stretch",
    },
    desktopStyles: {
      flexDirection: "row" as FlexDirectionType,
      alignItems: "stretch",
    },
  },
  related: {
    settings: ResponsiveContainerSettings,
  },
};
